import * as zod from "zod";
import type {UsuarioVm} from "@/modules/usuario";

export interface RequisicaoCompraVm {
    requisicaoCompraId: string;
    numeroRequisicao: number;
    finalidade: FinalidadeRequisicaoCompraVm;
    usuario: UsuarioVm;
    observacao: string | null;
    status: number;
    statusDescricao: string;
    dataRequisicao: Date;
    dataRequisicaoFormatada: string;
    dataAtualizacao: Date;
    dataAtualizacaoFormatada: string;
    anexos: RequisicaoCompraAnexoVm[];
    itens: RequisicaoCompraItemVm[];
}

/**
 * Item da requisição — espelha RequisicaoCompraItemVm. Desde o rename completo do backend (handoff
 * SOLICITACAO_GERAL_ITENS_ARTIGO_SERVICO.md, revisão de 2026-08-26) o item **é sempre** um Artigo já
 * cadastrado no catálogo — não existe mais `tipo`/`descricao` no contrato do item nem item de Serviço ou
 * Artigo avulso. O que não estiver no catálogo, ou for serviço, vai como texto na Observação do
 * cabeçalho da requisição (ver RequisicaoCompraItemCadastro.vue).
 */
export interface RequisicaoCompraItemVm {
    id: number;
    artigoId: number;
    artigoCodigoCompleto: string;
    artigoUnidade: string;
    descricao: string;
    quantidade: number;
    observacao: string | null;
}

export function dataAsRequisicaoCompraItemVm(data: any): RequisicaoCompraItemVm {
    return data as RequisicaoCompraItemVm;
}

/**
 * Estado local de um item ainda não gravado (ou já gravado, em edição) — não é enviado como está: o
 * `onGravar` de RequisicaoCompraCadastro.vue converte para `RequisicaoCompraItemCommand` na hora de
 * montar o FormData indexado (ver handoff SOLICITACAO_GERAL_ITENS_ARTIGO_SERVICO.md §3).
 */
export interface RequisicaoCompraItemFormVm {
    id?: number;
    artigoId: number;
    artigoCodigoCompleto?: string;
    artigoUnidade?: string;
    descricao: string;
    quantidade: number;
    observacao?: string;
}

export function dataAsRequisicaoCompraItemFormVm(data: any): RequisicaoCompraItemFormVm {
    return data as RequisicaoCompraItemFormVm;
}

/**
 * Converte o Vm vindo do backend (listar/consultar) para o estado local editável da tela — usado tanto
 * ao carregar uma requisição existente para edição (RequisicaoCompraCadastro.vue) quanto na tabela
 * somente-leitura de itens no expand da listagem (RequisicaoCompraLista.vue).
 */
export function converterParaRequisicaoCompraItemFormVm(item: RequisicaoCompraItemVm): RequisicaoCompraItemFormVm {
    return {
        id: item.id,
        artigoId: item.artigoId,
        artigoCodigoCompleto: item.artigoCodigoCompleto ?? undefined,
        artigoUnidade: item.artigoUnidade ?? undefined,
        descricao: item.descricao,
        quantidade: item.quantidade,
        observacao: item.observacao ?? undefined,
    };
}

export interface FinalidadeRequisicaoCompraVm {
    finalidadeId: number;
    descricao: string;
}

export interface RequisicaoCompraAnexoVm {
    id: number;
    nomeArquivo: string;
    tipoConteudoArquivo: string;
}

export function dataAsRequisicaoCompraVm(data: any): RequisicaoCompraVm {
    return data as RequisicaoCompraVm;
}

export enum EStatusRequisicaoCompra {
    Aberta = 1,
    Aprovada = 2,
    Cancelada = 3
}

export const zodFinalidadeVm = zod.object({
    finalidadeId: zod.number(),
    descricao: zod.string(),
}, {message: "Selecione uma finalidade"}).refine((val) => val != null);
